import { http } from "./http";
import { env } from "../config/env";
import { getAuthToken } from "../pages/AdminAuth/components/authStore";

function toUiStatus({ status, is_active, isActive }) {
  const active = is_active ?? isActive;
  if (active === false) return "Inactive";

  const s = String(status || "").toLowerCase();
  if (s.includes("inactive") || s.includes("banned")) return "Inactive";

  return "Active";
}

function isDeletedRow(e) {
  const s = String(e?.status || "").toLowerCase();
  return s === "deleted";
}

function pick(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return "";
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB");
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeEmployee(e, source = "admin_employee") {
  const onboardingComplete = pick(
    e?.is_onboarding_complete,
    e?.isOnboardingComplete
  );

  const approvalStatusRaw = pick(
    e?.approval_status,
    e?.approvalStatus,
    "NOT_SUBMITTED"
  );

  const approvalStatus = String(
    approvalStatusRaw || "NOT_SUBMITTED"
  ).toUpperCase();

  const permissions = Array.isArray(e?.permissions) ? e.permissions : [];

  const permissionOverrides = Array.isArray(e?.permission_overrides)
    ? e.permission_overrides
    : Array.isArray(e?.permissionOverrides)
    ? e.permissionOverrides
    : [];

  const permissionIds = Array.isArray(e?.permission_ids)
    ? e.permission_ids
    : Array.isArray(e?.permissionIds)
    ? e.permissionIds
    : [];

  const allowedWarehouseIds = Array.isArray(e?.allowed_warehouse_ids)
    ? e.allowed_warehouse_ids
    : Array.isArray(e?.allowedWarehouseIds)
    ? e.allowedWarehouseIds
    : [];

  const onboarding = e?.onboarding || {};
  const onboardingDocuments = onboarding?.documents || {};
  const onboardingPhotos = onboarding?.photos || {};
  const emergencyContact = onboarding?.emergencyContact || {};
  const referenceContacts = Array.isArray(onboarding?.referenceContacts)
    ? onboarding.referenceContacts
    : [];
  const onboardingAddress = onboarding?.address || {};

  const avatar = pick(
    e?.avatar,
    e?.profile?.avatar,
    e?.profile?.photo,
    e?.profile_pic,
    e?.profilePic,
    e?.employee_photo,
    e?.employeePhoto,
    onboardingDocuments?.profilePhoto?.url,
    onboardingDocuments?.employeePhoto?.url,
    onboardingDocuments?.photo1?.url,
    onboardingPhotos?.profilePhoto?.url,
    onboardingPhotos?.photo1?.url,
    onboardingPhotos?.photo2?.url
  );

  const onboardingLabel =
    onboardingComplete === true
      ? "Yes"
      : onboardingComplete === false
      ? "No"
      : "-";

  return {
    id: pick(e?.id, e?._id),
    status: toUiStatus(e),
    rawStatus: pick(e?.status, e?.user_status),
    name: pick(e?.name, e?.full_name, e?.fullName, "-"),
    email: pick(e?.email, "-"),
    phone: pick(e?.phone, "-"),
    role: pick(
      e?.role,
      e?.user_type,
      e?.userType,
      source === "warehouse_employee"
        ? "warehouse_employee"
        : source === "employee"
        ? "employee"
        : "admin"
    ),
    roleId: pick(e?.role_id, e?.roleId, ""),
    department: pick(e?.department, "-"),
    onboarding: onboardingLabel,
    onboardingStep: pick(e?.onboarding_step, e?.onboardingStep, 0),
    onboardingData: onboarding,
    onboardingNotes: pick(onboarding?.notes, "-"),
    onboardingAddress,
    onboardingDocuments,
    onboardingPhotos,
    emergencyContact,
    referenceContacts,
    excelKnowledgeConfirmed: Boolean(onboarding?.excelKnowledgeConfirmed),

    location: pick(
      e?.location,
      e?.address?.city,
      e?.address?.state,
      e?.address?.country,
      "-"
    ),

    dob: formatDate(pick(e?.dob, e?.date_of_birth, e?.dateOfBirth)),
    joiningDate: formatDate(
      pick(e?.joining_date, e?.joiningDate, e?.created_at, e?.createdAt)
    ),
    createdAt: formatDateTime(pick(e?.created_at, e?.createdAt)),
    updatedAt: formatDateTime(pick(e?.updated_at, e?.updatedAt)),
    lastLogin: formatDateTime(pick(e?.last_login, e?.lastLogin)),

    avatar,
    employeeId: pick(e?.employee_id, e?.employeeId, "-"),
    userType: pick(e?.user_type, e?.userType, source),
    gender: pick(e?.gender, "-"),
    bio: pick(e?.bio, "-"),

    designation: pick(e?.profile?.designation, "-"),
    profileNotes: pick(e?.profile?.notes, "-"),

    permissions,
    permissionOverrides,
    permissionIds,
    permissionsDisplay: permissions.length ? permissions.join(", ") : "-",
    permissionOverridesDisplay: permissionOverrides.length
      ? permissionOverrides.join(", ")
      : "-",

    address: e?.address || {},
    profile: e?.profile || {},

    approvalStatus,
    approvalLabel:
      approvalStatus === "APPROVED"
        ? "Approved"
        : approvalStatus === "REJECTED"
        ? "Rejected"
        : approvalStatus === "PENDING"
        ? "Pending"
        : "Not Submitted",

    isApproved: Boolean(e?.is_approved ?? e?.isApproved),
    approvalNote: pick(e?.approval_note, e?.approvalNote, ""),
    approvedBy: pick(e?.approved_by, e?.approvedBy, ""),
    approvedAt: formatDateTime(pick(e?.approved_at, e?.approvedAt)),

    canAdminPanel: Boolean(e?.can_admin_panel ?? e?.canAdminPanel),
    canAccessAdminDashboard: Boolean(
      e?.can_access_admin_dashboard ?? e?.canAccessAdminDashboard
    ),
    canAccessWarehouseDashboard: Boolean(
      e?.can_access_warehouse_dashboard ?? e?.canAccessWarehouseDashboard
    ),
    canSwitchToAdminDashboard: Boolean(
      e?.can_switch_to_admin_dashboard ?? e?.canSwitchToAdminDashboard
    ),
    canSwitchToWarehouseDashboard: Boolean(
      e?.can_switch_to_warehouse_dashboard ?? e?.canSwitchToWarehouseDashboard
    ),
    warehouseAccessMode: pick(
      e?.warehouse_access_mode,
      e?.warehouseAccessMode,
      "none"
    ),
    assignedWarehouseId: pick(
      e?.assigned_warehouse_id,
      e?.assignedWarehouseId,
      ""
    ),
    allowedWarehouseIds,

    documents: {
      employeePhoto: pick(
        e?.employee_photo,
        e?.employeePhoto,
        e?.profile?.photo,
        e?.profile?.avatar,
        onboardingDocuments?.profilePhoto?.url,
        onboardingDocuments?.employeePhoto?.url,
        onboardingDocuments?.photo1?.url,
        onboardingPhotos?.profilePhoto?.url,
        onboardingPhotos?.photo1?.url,
        onboardingPhotos?.photo2?.url,
        avatar
      ),
      aadhaarCard: pick(
        e?.aadhaar_card,
        e?.aadhaarCard,
        e?.aadhar_card,
        e?.aadharCard,
        onboardingDocuments?.aadhaar?.url
      ),
      panCard: pick(
        e?.pan_card,
        e?.panCard,
        onboardingDocuments?.pan?.url
      ),
      photo1: pick(
        onboardingDocuments?.photo1?.url,
        onboardingPhotos?.photo1?.url
      ),
      photo2: pick(
        onboardingDocuments?.photo2?.url,
        onboardingPhotos?.photo2?.url
      ),
    },

    _source: source,
    _raw: e,
  };
}


function formatLogDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB");
}

function formatLogTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function humanizeLogAction(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Activity recorded";

  return raw
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeActivityLog(item = {}, index = 0) {
  const createdAt = pick(item?.created_at, item?.createdAt, item?.timestamp);
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  const activity = pick(
    item?.activity,
    item?.description,
    meta?.message,
    meta?.description,
    meta?.summary,
    item?.action_label,
    item?.actionLabel,
    humanizeLogAction(item?.action)
  );

  return {
    id: pick(item?.id, item?._id, `${createdAt || "log"}-${index + 1}`),
    service: pick(item?.service, "auth"),
    action: pick(item?.action, ""),
    actorType: pick(item?.actor_type, item?.actorType, ""),
    actorId: pick(item?.actor_id, item?.actorId, ""),
    createdAt,
    date: formatLogDate(createdAt),
    time: formatLogTime(createdAt),
    activity,
    reqId: pick(item?.req_id, item?.reqId, ""),
    targetType: pick(item?.target_type, item?.targetType, ""),
    targetId: pick(item?.target_id, item?.targetId, ""),
    meta,
    _raw: item,
  };
}

function unwrapArray(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res)) return res;
  return [];
}

export const EmployeesService = {
  async listAdminEmployees(params = {}) {
    const res = await http("/api/admin/employees", { method: "GET", params });
    const data = unwrapArray(res);
    return data.filter((e) => !isDeletedRow(e));
  },

  async listEmployees(params = {}) {
    return this.listAdminEmployees(params);
  },

  async metaRoles() {
    const res = await http("/api/admin/employees/meta/roles", {
      method: "GET",
    });
    return unwrapArray(res);
  },

  async getRolesMeta() {
    return this.metaRoles();
  },

  async metaPermissions() {
    const res = await http("/api/admin/employees/meta/permissions", {
      method: "GET",
    });
    return unwrapArray(res);
  },

  async getPermissionsMeta() {
    return this.metaPermissions();
  },

  async createAdminEmployee(payload) {
    return http("/api/admin/employees", {
      method: "POST",
      body: payload,
    });
  },

  async createEmployee(payload) {
    return this.createAdminEmployee(payload);
  },

  async updateEmployee(id, payload) {
    return http(`/api/admin/employees/${id}`, {
      method: "PUT",
      body: payload,
    });
  },

  async deleteAdminEmployee(id) {
    return http(`/api/admin/employees/${id}`, { method: "DELETE" });
  },

  async deleteEmployee(id) {
    return this.deleteAdminEmployee(id);
  },

  async activateEmployee(id) {
    return http(`/api/admin/employees/${id}/activate`, {
      method: "PATCH",
    });
  },

  async deactivateEmployee(id) {
    return http(`/api/admin/employees/${id}/deactivate`, {
      method: "PATCH",
    });
  },

  async approveEmployee(id, payload) {
    return http(`/api/admin/employees/${id}/approval`, {
      method: "PATCH",
      body: payload,
    });
  },

  async updateApproval(id, payload) {
    return this.approveEmployee(id, payload);
  },

  async getApprovalSummary(params = {}) {
    const res = await http("/api/admin/employees/approval-summary", {
      method: "GET",
      params,
    });

    const payload = res?.data?.data || res?.data || res || {};

    return {
      total: Number(payload?.total || 0),
      notSubmitted: Number(
        payload?.notSubmitted ?? payload?.notsubmitted ?? 0
      ),
      pending: Number(payload?.pending || 0),
      approved: Number(payload?.approved || 0),
      rejected: Number(payload?.rejected || 0),
    };
  },

  async listAllForDashboard(params = {}) {
    const items = await this.listAdminEmployees(params);

    return items.map((e) => {
      const userType = String(e?.user_type || e?.userType || "").toLowerCase();

      let source = "admin_employee";
      if (userType === "warehouse_employee") source = "warehouse_employee";
      else if (userType === "employee") source = "employee";
      else if (userType === "admin") source = "admin_employee";

      return normalizeEmployee(e, source);
    });
  },



  async getActivityLogs(params = {}) {
    const res = await http("/api/admin/logs", {
      method: "GET",
      params,
    });

    const payload = res?.data?.data || res?.data || res || {};
    const rows = unwrapArray(payload);

    const items = rows.map((item, index) => normalizeActivityLog(item, index));

    return {
      items,
      page: Number(payload?.page || params?.page || 1),
      limit: Number(payload?.limit || params?.limit || items.length || 0),
      total: Number(
        payload?.total ?? payload?.count ?? payload?.meta?.total ?? items.length
      ),
      raw: payload,
    };
  },

  async getEmployeeActivityLogs(id, params = {}) {
    return this.getActivityLogs({
      actorId: id,
      ...params,
    });
  },

  async getEmployeeById(id) {
    const res = await http(`/api/admin/employees/${id}`, { method: "GET" });

    const data = res?.data?.data || res?.data || res;

    if (!data) return null;

    const userType = String(
      data?.user_type || data?.userType || ""
    ).toLowerCase();

    let source = "admin_employee";
    if (userType === "warehouse_employee") source = "warehouse_employee";
    else if (userType === "employee") source = "employee";
    else if (
      userType === "admin" ||
      userType === "admin_employee"
    ) {
      source = "admin_employee";
    }

    return normalizeEmployee(data, source);
  },

  async updateOnboarding(payload = {}) {
  const formData = new FormData();

  if (payload.onboardingStep !== undefined && payload.onboardingStep !== null) {
    formData.append("onboardingStep", String(payload.onboardingStep));
  }

  if (
    payload.isOnboardingComplete !== undefined &&
    payload.isOnboardingComplete !== null
  ) {
    formData.append(
      "isOnboardingComplete",
      String(payload.isOnboardingComplete)
    );
  }

  if (payload.emergencyContact) {
    formData.append(
      "emergencyContact",
      JSON.stringify(payload.emergencyContact)
    );
  }

  if (payload.referenceContacts) {
    formData.append(
      "referenceContacts",
      JSON.stringify(payload.referenceContacts)
    );
  }

  if (
    payload.excelKnowledgeConfirmed !== undefined &&
    payload.excelKnowledgeConfirmed !== null
  ) {
    formData.append(
      "excelKnowledgeConfirmed",
      String(payload.excelKnowledgeConfirmed)
    );
  }

  if (payload.aadhaarFile) {
    formData.append("documents.aadhaar", payload.aadhaarFile);
  }

  if (payload.panFile) {
    formData.append("documents.pan", payload.panFile);
  }

  if (payload.profilePhotoFile) {
    formData.append("documents.profilePhoto", payload.profilePhotoFile);
  }

  return http("/api/auth/onboarding", {
    method: "PATCH",
    body: formData,
  });
},

updateEmployeeOnboarding: async (id, formData) => {
  const token = getAuthToken?.() || "";
  const base =
    env?.AUTH_BASE_URL ||
    process.env.REACT_APP_AUTH_BASE_URL ||
    "";

  const res = await fetch(`${base}/api/admin/employees/${id}/onboarding`, {
    method: "PATCH",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text || "Invalid response" };
  }

  if (!res.ok) {
    throw new Error(data?.message || "Failed to update onboarding");
  }

  return data?.data || data;
},
};